'use client';

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { logUxApiRequestId } from '@/lib/api/log-api-request-correlation';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

export type User = {
  id: string;
  email: string;
  emailVerified?: boolean;
  profileImageUrl?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  streetAddress?: string | null;
  city?: string | null;
  province?: string | null;
  postalCode?: string | null;
  dateOfBirth?: string | null;
  covaCustomerId?: string | null;
  communicationPreferences?: {
    emailTransactionalOptIn: boolean;
    emailPromotionalOptIn: boolean;
    smsTransactionalOptIn: boolean;
    smsPromotionalOptIn: boolean;
    pushTransactionalOptIn: boolean;
    pushPromotionalOptIn: boolean;
    communicationPreferencesUpdatedAt: string | null;
  };
};

type AuthResult = {
  success: boolean;
  error?: string;
  requiresEmailVerification?: boolean;
  email?: string;
  emailSent?: boolean;
};

type AuthPayload = {
  success?: boolean;
  user?: User;
  accessToken?: string;
  refreshToken?: string;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<AuthResult>;
  signUp: (data: SignUpData, rememberMe?: boolean) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  updateUser: (data: Partial<User>) => Promise<{ success: boolean; error?: string }>;
  applyAuthSession: (payload: AuthPayload) => void;
};

type SignUpData = {
  email: string;
  password: string;
  firstName: string;
  lastName?: string;
  phone?: string;
  streetAddress?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  dateOfBirth?: string;
  marketingOptIn?: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const applyAuthSession = useCallback((payload: AuthPayload) => {
    if (!payload?.accessToken || !payload?.refreshToken || !payload?.user) {
      return;
    }

    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('dank:pending-login-cart-merge', '1');
      }
    } catch {
      /* ignore */
    }

    localStorage.setItem('accessToken', payload.accessToken);
    localStorage.setItem('refreshToken', payload.refreshToken);
    setUser(payload.user);
  }, []);

  // Load user from token on mount
  useEffect(() => {
    loadUser();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (!user) return;

    // Refresh token every 14 minutes (before 15 min expiry)
    const interval = setInterval(() => {
      refreshToken().catch(() => {
        // Silent fail, will retry on next request
      });
    }, 14 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  const loadUser = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      let data: Record<string, unknown> = {};
      try {
        data = (await response.json()) as Record<string, unknown>;
      } catch {
        data = {};
      }

      if (!response.ok) {
        logUxApiRequestId('auth_me', response, 'http_error');
      } else if (!data.success) {
        logUxApiRequestId('auth_me', response, 'business_error');
      }

      if (response.ok) {
        if (data.success && data.user) {
          setUser(data.user as User);
        } else {
          // Try to refresh token
          const refreshed = await refreshToken();
          if (!refreshed) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setUser(null);
          }
        }
      } else if (response.status === 401) {
        // Token expired, try to refresh
        const refreshed = await refreshToken();
        if (!refreshed) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setUser(null);
        }
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setUser(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading user:', error);
      }
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (isRefreshing) return false;
    
    try {
      setIsRefreshing(true);
      const refreshTokenValue = localStorage.getItem('refreshToken');
      
      if (!refreshTokenValue) {
        return false;
      }

      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
        credentials: 'include',
      });

      let data: Record<string, unknown> = {};
      try {
        data = (await response.json()) as Record<string, unknown>;
      } catch {
        data = {};
      }

      if (!response.ok) {
        logUxApiRequestId('auth_refresh', response, 'http_error');
      } else if (!data.success) {
        logUxApiRequestId('auth_refresh', response, 'business_error');
      }

      if (response.ok) {
        if (data.success && data.accessToken) {
          localStorage.setItem('accessToken', data.accessToken as string);
          
          // Reload user data
          const userResponse = await fetch(`${API_BASE}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${data.accessToken as string}`,
            },
            credentials: 'include',
          });

          let userData: Record<string, unknown> = {};
          try {
            userData = (await userResponse.json()) as Record<string, unknown>;
          } catch {
            userData = {};
          }
          if (!userResponse.ok) {
            logUxApiRequestId('auth_me_after_refresh', userResponse, 'http_error');
          } else if (!userData.success) {
            logUxApiRequestId('auth_me_after_refresh', userResponse, 'business_error');
          }

          if (userResponse.ok) {
            if (userData.success && userData.user) {
              setUser(userData.user as User);
            }
          }
          
          return true;
        }
      }
      
      // Refresh failed, clear tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error refreshing token:', error);
      }
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  const signIn = useCallback(async (
    email: string,
    password: string,
    rememberMe: boolean = false
  ): Promise<AuthResult> => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, rememberMe }),
        credentials: 'include',
      });

      const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

      if (!response.ok) {
        logUxApiRequestId('auth_signin', response, 'http_error');
      } else if (!data.success && !data.requiresEmailVerification) {
        logUxApiRequestId('auth_signin', response, 'business_error');
      }

      if (data.success && data.accessToken && data.refreshToken && data.user) {
        applyAuthSession(data as AuthPayload);
        return { success: true };
      }

      if (data?.requiresEmailVerification) {
        return {
          success: false,
          error: (data.error as string) || 'Please verify your email before signing in.',
          requiresEmailVerification: true,
          email: (data.email as string) || email,
          emailSent: data.emailSent as boolean | undefined,
        };
      }

      return { success: false, error: (data.error as string) || 'Sign in failed' };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred during sign in';
      return { success: false, error: errorMessage };
    }
  }, [applyAuthSession]);

  const signUp = useCallback(async (
    signUpData: SignUpData,
    rememberMe: boolean = false
  ): Promise<AuthResult> => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...signUpData, rememberMe }),
        credentials: 'include',
      });

      const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

      if (!response.ok) {
        logUxApiRequestId('auth_signup', response, 'http_error');
      } else if (data.success === false) {
        logUxApiRequestId('auth_signup', response, 'business_error');
      }

      if (data.success && data.accessToken && data.refreshToken && data.user) {
        applyAuthSession(data as AuthPayload);
        return { success: true };
      }

      if (data?.success && data?.requiresEmailVerification) {
        return {
          success: true,
          requiresEmailVerification: true,
          email: (data.email as string) || signUpData.email,
          emailSent: data.emailSent as boolean | undefined,
        };
      }

      return { success: false, error: (data.error as string) || 'Sign up failed' };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred during sign up';
      return { success: false, error: errorMessage };
    }
  }, [applyAuthSession]);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      
      await fetch(`${API_BASE}/api/auth/signout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
        credentials: 'include',
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error signing out:', error);
      }
    } finally {
      try {
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('dank:pending-login-cart-merge');
        }
      } catch {
        /* ignore */
      }
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    }
  }, []);

  const updateUser = useCallback(async (
    userData: Partial<User>
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/auth/account`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(userData),
        credentials: 'include',
      });

      const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

      if (!response.ok) {
        logUxApiRequestId('auth_account_update', response, 'http_error');
      } else if (!data.success) {
        logUxApiRequestId('auth_account_update', response, 'business_error');
      }

      if (data.success && data.user) {
        setUser(data.user as User);
        return { success: true };
      } else {
        // Try to refresh token and retry
        const refreshed = await refreshToken();
        if (refreshed) {
          return updateUser(userData);
        }
        return { success: false, error: (data.error as string) || 'Update failed' };
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while updating account';
      return { success: false, error: errorMessage };
    }
  }, [refreshToken]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        signIn,
        signUp,
        signOut,
        refreshToken,
        updateUser,
        applyAuthSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

